import { SubmitEvent, ReactNode, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { config } from '@/config';
import { SecondFactorPrompt } from './SecondFactorPrompt';
import { PasskeySignInButton } from './PasskeySignInButton';
import { MfaChallenge, isMfaChallenge } from './mfaChallenge';
import { completeSignIn } from '@/lib/activeAccount';
import { tooManyAccountsMessage } from './tooManyAccounts';

interface CredentialsSignInFormProps {
  /** Pins the address and renders it read-only — for flows that already know
   *  who is signing in, such as accepting an invitation. */
  fixedEmail?: string;
  /** Prefills the address but leaves it editable: signing a listed account back in. */
  initialEmail?: string;
  submitLabel?: string;
  pendingLabel?: string;
  /** Rendered above the fields: a note explaining why this form is here. */
  intro?: ReactNode;
}

/**
 * Email + password sign-in.
 *
 * Success is deliberately not a callback. Signing in invalidates
 * `currentUser`, and every caller already reacts to the refreshed user —
 * SignIn navigates to `returnTo`, AcceptInvitation accepts the invitation for
 * the now-authenticated user. Routing the outcome through auth state rather
 * than a prop keeps one description of "what happens after sign-in" instead of
 * one per caller.
 */
export function CredentialsSignInForm({
  fixedEmail,
  initialEmail,
  submitLabel = 'Sign in',
  pendingLabel = 'Signing in…',
  intro,
}: CredentialsSignInFormProps) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState(fixedEmail ?? initialEmail ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [challenge, setChallenge] = useState<MfaChallenge | undefined>(undefined);

  const onSubmit = (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(undefined);
    setPending(true);
    void axios
      .post(`${config.backendApi}/api/auth/login`, { email, password }, { withCredentials: true })
      .then(
        (res) => {
          // ⚠ **A 200 here is not necessarily a session.** A user who holds a second factor
          // gets `mfaRequired` and no cookie; invalidating `currentUser` on that would refetch
          // an unauthenticated user and leave the form sitting there having apparently
          // succeeded — silently, and only for the people who opted into protection.
          if (isMfaChallenge(res.data)) {
            setChallenge(res.data);
            return undefined;
          }
          return completeSignIn(queryClient, res.data);
        },
        (e: unknown) => {
          // One message for a bad address and a bad password alike: naming which
          // was wrong tells an unauthenticated caller whether an account exists.
          setError(tooManyAccountsMessage(e) ?? 'Invalid username or password');
        },
      )
      .finally(() => { setPending(false); });
  };

  if (challenge) {
    return (
      <SecondFactorPrompt
        challenge={challenge}
        onCancel={() => { setChallenge(undefined); setPassword(''); }}
      />
    );
  }

  return (
    <form onSubmit={onSubmit} className="p-6 space-y-4">
      {intro}

      <div className="space-y-1.5">
        <Label htmlFor="signin-email">Email</Label>
        <Input
          id="signin-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          readOnly={fixedEmail !== undefined}
          disabled={fixedEmail !== undefined || pending}
          onChange={(e) => { setEmail(e.target.value); }}
        />
      </div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between">
          <Label htmlFor="signin-password">Password</Label>
          <Link
            to={fixedEmail ? `/account/forgot?email=${encodeURIComponent(fixedEmail)}` : '/account/forgot'}
            className="text-xs text-primary hover:underline"
          >
            Forgot?
          </Link>
        </div>
        <Input
          id="signin-password"
          type="password"
          autoComplete="current-password"
          required
          autoFocus={fixedEmail !== undefined}
          value={password}
          disabled={pending}
          onChange={(e) => { setPassword(e.target.value); }}
        />
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Button type="submit" className="w-full" disabled={pending}>
        {pending ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            {pendingLabel}
          </>
        ) : (
          submitLabel
        )}
      </Button>
      <div className="relative py-1 text-center">
        <span className="bg-background px-2 text-xs text-muted-foreground">or</span>
      </div>
      <PasskeySignInButton email={email} />

    </form>
  );
}
